import React from 'react';
import { Avatar, Tooltip } from '@mui/material';
import classNames from 'classnames';
import TagsView from 'js/components/elements/tags/TagsView';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { Stack } from 'new/elements/Stack';
import { bytesToString, formatNamedNumber, formatNumber } from 'js/lib/FormatUtils';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_FLOW_METRICS_REQUEST } from 'js/constants/actionTypes';
import UIUtils from 'js/lib/UIUtils';
import { isOwner } from 'js/lib/ACLUtils';
import StatsTooltipView from 'js/components/pipelines/cardUtils/StatsTooltipView';
import {
  useTriageLauncher,
  buildTriageContextFromResource,
} from 'new/components/monitor/TriageChat';

// SVG Icons
import { ReactComponent as ArrowRightIcon } from 'new/components/icons/fi-rr-arrow-small-right.svg';

// Styles
import rootStyles from './ResourceCard.module.scss';
import styles from 'js/components/pipelines/cards/InfoContentDiscoveryMode.module.scss';

interface InfoContentDiscoveryModeProps {
  data: any;
  owner: Record<string, any>;
  errorCount: number;
  recordCount: number;
  recordVolume: number;
  dataProcessedLabel?: string;
  statusBadge?: React.ReactNode;
  omitTags?: boolean;
  omitOwnerLabel?: boolean;
  omitStatusBadge?: boolean;
  omitProcessedDataInformation?: boolean;
}

export const InfoContentDiscoveryMode = (props: InfoContentDiscoveryModeProps) => {
  const {
    data,
    owner,
    errorCount,
    recordCount,
    recordVolume,
    statusBadge = null,
    dataProcessedLabel = '24h data',

    omitTags,
    omitOwnerLabel,
    omitStatusBadge,
    omitProcessedDataInformation = false,
  } = props;

  const loading = useIsLoading(GET_FLOW_METRICS_REQUEST);
  const triage = useTriageLauncher();

  const errorLabel = UIUtils.toPluralFormat('error', errorCount, false);
  const recordOrVolumeDisplay = (() => {
    if (loading) return '...';
    if (recordCount) return `${formatNamedNumber(recordCount)} rec.`;
    if (recordVolume) return `${bytesToString(recordVolume)}`;
    return '';
  })();

  return (
    <Stack gap="6px" className={rootStyles.idContainer} data-discovery-mode="true">
      {/* Data processed information - only show when needed */}
      {!omitProcessedDataInformation && (
        <Stack direction="row" gap="4px" alignItems="center" justifyContent="flex-end">
          <span className={styles.dataProcessedTitle}>{dataProcessedLabel}</span>
          <Tooltip title="Title">
            <StatsTooltipView />
          </Tooltip>
        </Stack>
      )}

      {/* Main info row: ID + Record/Volume count */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <div data-testid={`info-content-id-${data?.id}`}>
          <ChipWithCopy toCopy={data?.id}>ID {data?.id}</ChipWithCopy>
        </div>
        <span className={styles.dataProcessed}>{recordOrVolumeDisplay}</span>
      </Stack>

      {/* Status and errors row: Status badge + Error count */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {!omitStatusBadge && statusBadge}

        {loading ? (
          <span className={classNames(styles.count)}>...</span>
        ) : errorCount ? (
          <span className={classNames(styles.count, rootStyles.errorCount)}>
            {formatNumber(errorCount)} {errorLabel}
          </span>
        ) : (
          // If no errors, still maintain layout balance
          <span className={styles.count}>0 err.</span>
        )}
      </Stack>

      {/* Optional elements */}
      {!omitTags && <TagsView data={data} />}

      {!omitOwnerLabel && !isOwner(data) && owner && (
        <Stack gap="6px" flexWrap="wrap" direction="row" alignItems="center" justifyContent="start">
          <Typography className={rootStyles.ownedByLabel}>Owned by</Typography>
          <Avatar className={rootStyles.ownedByAvatar} sx={{ width: 16, height: 16 }}>
            {owner.name
              .split(' ')
              .slice(0, 2)
              .map((group: string) => group[0]?.toUpperCase?.())
              .join('')}
          </Avatar>
          <Typography className={rootStyles.ownedByEmail}>{owner.email}</Typography>
        </Stack>
      )}
      {/* Triage button row - only rendered when triage feature is enabled */}
      {triage.enabled && (
        <div className={styles.triageContainer}>
          <span
            className={styles.triageButton}
            role="button"
            tabIndex={0}
            data-testid="triage-node-button"
            onClick={() =>
              triage.open(
                buildTriageContextFromResource({
                  resourceType: data?.type,
                  resourceId: data?.id,
                  resourceName: data?.name,
                }),
                'flow_dag_node',
              )
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                triage.open(
                  buildTriageContextFromResource({
                    resourceType: data?.type,
                    resourceId: data?.id,
                    resourceName: data?.name,
                  }),
                  'flow_dag_node',
                );
              }
            }}
          >
            Triage
            <ArrowRightIcon />
          </span>
        </div>
      )}
    </Stack>
  );
};
